import type { StartupDiagnosticRecord } from "@/lib/startupDiagnostics";

export const STARTUP_MOUNT_EVENT = "recipmon:app-mounted";
export const STARTUP_RECOVERY_OVERLAY_ID = "recipmon-startup-recovery-overlay";
export const TELEGRAM_STARTUP_WATCHDOG_TIMEOUT_MS = 3500;
export const DEFAULT_STARTUP_WATCHDOG_TIMEOUT_MS = 5000;

type WindowLike = Pick<
  Window,
  "setTimeout" | "clearTimeout" | "addEventListener" | "removeEventListener" | "dispatchEvent"
> & {
  location: Pick<Location, "reload" | "assign" | "href">;
};

type DocumentLike = Pick<Document, "createElement" | "getElementById"> & {
  body: HTMLElement | null;
};

export type StartupWatchdogOptions = {
  pathname: string;
  isTelegramContext: boolean;
  windowRef?: WindowLike;
  documentRef?: DocumentLike;
  timeoutMs?: number;
  getStartupDiagnostic?: () => StartupDiagnosticRecord | null;
  formatDiagnostic?: (record: StartupDiagnosticRecord | null) => string | null;
  onResetAppCache?: () => Promise<unknown> | unknown;
  openMiniAppPath?: string;
};

export type StartupWatchdogController = {
  cancel: () => void;
  showNow: () => void;
};

export const shouldStartStartupWatchdog = (pathname: string) => pathname !== "/";

export const getStartupWatchdogTimeoutMs = (isTelegramContext: boolean) =>
  isTelegramContext ? TELEGRAM_STARTUP_WATCHDOG_TIMEOUT_MS : DEFAULT_STARTUP_WATCHDOG_TIMEOUT_MS;

const ensureRecoveryOverlay = (
  documentRef: DocumentLike,
  windowRef: WindowLike,
  options: Pick<
    StartupWatchdogOptions,
    "getStartupDiagnostic" | "formatDiagnostic" | "onResetAppCache" | "openMiniAppPath"
  >,
) => {
  const existing = documentRef.getElementById(STARTUP_RECOVERY_OVERLAY_ID);
  if (existing) {
    return existing as HTMLDivElement;
  }

  if (!documentRef.body) {
    return null;
  }

  const overlay = documentRef.createElement("div");
  overlay.id = STARTUP_RECOVERY_OVERLAY_ID;
  overlay.setAttribute("role", "dialog");
  overlay.setAttribute("aria-live", "polite");
  overlay.style.position = "fixed";
  overlay.style.inset = "0";
  overlay.style.zIndex = "2147483647";
  overlay.style.display = "flex";
  overlay.style.alignItems = "center";
  overlay.style.justifyContent = "center";
  overlay.style.padding = "16px";
  overlay.style.background = "#ffffff";
  overlay.style.color = "#111827";
  overlay.style.fontFamily =
    "\"Space Grotesk\", ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, \"Segoe UI\", sans-serif";

  const card = documentRef.createElement("div");
  card.style.width = "100%";
  card.style.maxWidth = "560px";
  card.style.background = "#ffffff";
  card.style.border = "2px solid #111827";
  card.style.borderRadius = "16px";
  card.style.padding = "18px";
  card.style.boxShadow = "8px 8px 0px 0px rgba(17,24,39,1)";

  const title = documentRef.createElement("h1");
  title.textContent = "Recipmon is taking too long to load";
  title.style.margin = "0";
  title.style.fontSize = "18px";
  title.style.fontWeight = "800";

  const desc = documentRef.createElement("p");
  desc.textContent =
    "The app may be stuck on an old cache or a startup error. Try reloading, or reset the app cache if this keeps happening.";
  desc.style.margin = "10px 0 0 0";
  desc.style.fontSize = "13px";
  desc.style.lineHeight = "1.45";

  const diagnosticText =
    options.formatDiagnostic?.(options.getStartupDiagnostic?.() ?? null) ?? null;
  const debug = documentRef.createElement("p");
  debug.style.margin = "12px 0 0 0";
  debug.style.fontSize = "12px";
  debug.style.color = "#4B5563";
  debug.style.whiteSpace = "normal";
  if (diagnosticText) {
    debug.textContent = `Last startup error: ${diagnosticText}`;
  } else {
    debug.textContent = "Last startup error: unknown_startup_timeout";
  }

  const actions = documentRef.createElement("div");
  actions.style.display = "flex";
  actions.style.flexWrap = "wrap";
  actions.style.gap = "8px";
  actions.style.marginTop = "14px";

  const makeButton = (label: string) => {
    const button = documentRef.createElement("button");
    button.type = "button";
    button.textContent = label;
    button.style.border = "2px solid #111827";
    button.style.borderRadius = "10px";
    button.style.padding = "9px 12px";
    button.style.background = "#ffffff";
    button.style.color = "#111827";
    button.style.fontWeight = "800";
    button.style.cursor = "pointer";
    return button;
  };

  const reloadButton = makeButton("Reload App");
  reloadButton.addEventListener("click", () => {
    try {
      windowRef.location.reload();
    } catch {
      // ignore
    }
  });

  const openMiniAppButton = makeButton("Open Mini App Route");
  openMiniAppButton.addEventListener("click", () => {
    const target = options.openMiniAppPath || "/recipmontelegram/";
    try {
      windowRef.location.assign(target);
    } catch {
      // ignore
    }
  });

  const resetCacheButton = makeButton("Reset App Cache");
  if (!options.onResetAppCache) {
    resetCacheButton.disabled = true;
    resetCacheButton.style.opacity = "0.6";
    resetCacheButton.style.cursor = "not-allowed";
  } else {
    resetCacheButton.addEventListener("click", async () => {
      if (resetCacheButton.disabled) {
        return;
      }
      resetCacheButton.disabled = true;
      const originalLabel = resetCacheButton.textContent;
      resetCacheButton.textContent = "Resetting...";
      try {
        await options.onResetAppCache();
      } catch (error) {
        resetCacheButton.disabled = false;
        resetCacheButton.textContent = originalLabel ?? "Reset App Cache";
        const message =
          error instanceof Error ? error.message : String(error ?? "Unknown reset error");
        debug.textContent = `Last startup error: reset_failed: ${message}`;
      }
    });
  }

  actions.appendChild(reloadButton);
  actions.appendChild(openMiniAppButton);
  actions.appendChild(resetCacheButton);

  card.appendChild(title);
  card.appendChild(desc);
  card.appendChild(debug);
  card.appendChild(actions);
  overlay.appendChild(card);
  documentRef.body.appendChild(overlay);

  return overlay;
};

export const startStartupWatchdog = (options: StartupWatchdogOptions): StartupWatchdogController => {
  const windowRef = options.windowRef ?? (window as unknown as WindowLike);
  const documentRef = options.documentRef ?? (document as unknown as DocumentLike);

  if (!shouldStartStartupWatchdog(options.pathname)) {
    return {
      cancel: () => undefined,
      showNow: () => undefined,
    };
  }

  let timeoutId: number | null = null;
  let cancelled = false;

  const showOverlay = () => {
    if (cancelled) {
      return;
    }
    void ensureRecoveryOverlay(documentRef, windowRef, options);
  };

  const handleMounted = () => {
    if (cancelled) {
      return;
    }
    cancelled = true;
    if (timeoutId !== null) {
      windowRef.clearTimeout(timeoutId);
      timeoutId = null;
    }
    const existing = documentRef.getElementById(STARTUP_RECOVERY_OVERLAY_ID);
    if (existing) {
      existing.remove();
    }
    windowRef.removeEventListener(STARTUP_MOUNT_EVENT, handleMounted);
  };

  windowRef.addEventListener(STARTUP_MOUNT_EVENT, handleMounted);
  timeoutId = windowRef.setTimeout(
    showOverlay,
    options.timeoutMs ?? getStartupWatchdogTimeoutMs(options.isTelegramContext),
  ) as unknown as number;

  return {
    cancel: handleMounted,
    showNow: showOverlay,
  };
};

